import React, { useEffect, useState, useRef } from "react";
import "./unitPaymentForm.css";
import {
  CreditCard,
  PaymentForm,
} from "react-square-web-payments-sdk";

class PaymentFormErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error) {
    this.props.onError?.(error);
  }

  componentDidUpdate(previousProps) {
    if (previousProps.resetKey !== this.props.resetKey && this.state.error) {
      this.setState({ error: null });
    }
  }

  render() {
    if (this.state.error) {
      return (
        <div className="payment-status payment-error">
          Secure payment form could not be loaded. Please refresh the page or contact us to finish your booking.
        </div>
      );
    }

    return this.props.children;
  }
}

// tile that shows unit information
function UnitPaymentForm({
  totals,
  setStep,
  squareCredentials,
  squareCredentialsIsLoading,
  squareCredentialsError,
  selectedSlots,
  terms,
  handleBookingSubmission
}) {
  const cardRef = useRef(null);
  const termsRef = useRef(null);
  const paymentFormRef = useRef(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [jiggleTerms, setJiggleTerms] = useState(false);
  const [squareFrameReady, setSquareFrameReady] = useState(false);
  const [squareLoadTimedOut, setSquareLoadTimedOut] = useState(false);
  const [squareRenderError, setSquareRenderError] = useState("");

  const needsPayment = Number(totals.deposit || 0) > 0;
  const squareReady = Boolean(squareCredentials?.app_id && squareCredentials?.location_id);
  const squareFormKey = squareReady
    ? `${squareCredentials.app_id}-${squareCredentials.location_id}`
    : "missing-square-config";

  useEffect(() => {
    if (!cardRef.current) return;

    window.scrollTo({
      top: cardRef.current.offsetTop,
      left: 0,
      behavior: "smooth",
    });
  }, []);

  useEffect(() => {
    setSquareFrameReady(false);
    setSquareLoadTimedOut(false);
    setSquareRenderError("");

    if (!needsPayment || !squareReady || typeof MutationObserver === "undefined") return undefined;

    const checkForSquareFrame = () => {
      const hasSquareFrame = Boolean(paymentFormRef.current?.querySelector("iframe"));
      if (hasSquareFrame) {
        setSquareFrameReady(true);
        setSquareLoadTimedOut(false);
      }
      return hasSquareFrame;
    };

    const observer = new MutationObserver(checkForSquareFrame);
    if (paymentFormRef.current) {
      observer.observe(paymentFormRef.current, { childList: true, subtree: true });
    }

    const timeoutId = window.setTimeout(() => {
      if (!checkForSquareFrame()) {
        setSquareLoadTimedOut(true);
      }
    }, 10000);

    checkForSquareFrame();

    return () => {
      observer.disconnect();
      window.clearTimeout(timeoutId);
    };
  }, [needsPayment, squareReady, squareFormKey]);

  const promptForTerms = () => {
    setJiggleTerms(true);
    window.setTimeout(() => {
      setJiggleTerms(false);
    }, 500);
  };

  const handleFreeBooking = () => {
    if (!termsAccepted) {
      promptForTerms();
      return;
    }

    handleBookingSubmission(null, 0, true);
  };

  return (
    <div ref={cardRef} className="payment-container main-border" >
      <div className="payment-heading">
        <p id="payment-title">Payment</p>

        <div className="instructions">
          Please enter your payment information below.
        </div>

        <div className="deposit">
          <span>${totals.deposit} Due Today</span>
        </div>

        <div className={`terms-box ${jiggleTerms && "shake"}`}>
          <input
            ref={termsRef}
            type="checkbox"
            id="terms"
            checked={termsAccepted}
            onChange={(event) => setTermsAccepted(event.target.checked)}
          />
          <label htmlFor="terms" id="terms-checkbox">
            I agree to the{" "}
            {terms ? (
              <a href={terms} target="_blank" rel="noreferrer noopener" className="">
                Terms and Conditions
              </a>
            ) : (
              <span>Terms and Conditions</span>
            )}
            <span className="error-text">*</span>
          </label>
        </div>
      </div>


      
      

      {!isSubmitting && !needsPayment && (
        <button
          className="btn btn-primary w-100"
          onClick={handleFreeBooking}
        >
          Complete Booking
        </button>
      )}

      {!isSubmitting && needsPayment && <div className="payment-form" ref={paymentFormRef}>
        {squareCredentialsIsLoading && (
          <div className="payment-status">Loading secure payment form...</div>
        )}

        {!squareCredentialsIsLoading && (!squareReady || squareCredentialsError) && (
          <div className="payment-status payment-error">
            Secure payment form could not be loaded. Please refresh the page or contact us to finish your booking.
          </div>
        )}

        {squareReady && (
          <PaymentFormErrorBoundary
            resetKey={squareFormKey}
            onError={(error) => setSquareRenderError(error?.message || "Payment form failed to load.")}
          >
            {!termsAccepted && (
              <div className="payment-status payment-info">
                Check the terms box above to enable payment.
              </div>
            )}

            {squareLoadTimedOut && !squareFrameReady && !squareRenderError && (
              <div className="payment-status payment-warning">
                Payment fields are taking longer than expected to load. Refresh the page, disable content blockers, or contact us to finish your booking.
              </div>
            )}

            {squareRenderError && (
              <div className="payment-status payment-error">
                Secure payment form could not be loaded. Please refresh the page or contact us to finish your booking.
              </div>
            )}

            <PaymentForm
              key={squareFormKey}
              applicationId={squareCredentials.app_id}
              cardTokenizeResponseReceived={ async (token, verifiedBuyer) => {
                if (isSubmitting || !termsAccepted) return;

                setIsSubmitting(true);

                try {
                  await handleBookingSubmission(token.token, totals.deposit * 100, false, verifiedBuyer);
                  return;
                } catch (err) {
                  console.error("Payment error:", err);
                  setIsSubmitting(false);
                  return;
                }
              }}
              locationId={squareCredentials.location_id}
              createPaymentRequest={() => ({
                countryCode: "US",
                currencyCode: "USD",
                total: {
                  amount: String(totals.deposit),
                  label: "Deposit",
                },
              })}
            >
              <CreditCard
                focus={termsAccepted ? "cardNumber" : undefined}
                buttonProps={{
                  isLoading: isSubmitting || !termsAccepted,
                }}
              />
            </PaymentForm>
          </PaymentFormErrorBoundary>
        )}
      </div>}
    </div>
  );
}

export default UnitPaymentForm;
